/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import { RepoHealthStatus } from './types';

// Outcome + age folded into one legible verdict per repo (PLAT-1702): the
// tone drives the list panel's health dot, the label its tooltip. Builds
// are fail-silent in CI, so this readout is where a silently failing repo
// stops looking identical to a healthy quiet one.
export type RepoHealth = {
  tone: 'ok' | 'stale' | 'attention' | 'failed' | 'none';
  label: string;
};

export const STALE_AFTER_DAYS = 20;
const DAY_MS = 24 * 60 * 60 * 1000;

const ageDays = (
  iso: string | null | undefined,
  nowMs: number,
): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((nowMs - t) / DAY_MS));
};

const agoLabel = (d: number) => (d === 0 ? 'today' : `${d}d ago`);

// `updatedAt` (the store pointer's own mtime) is the age fallback for repos
// that haven't built since the status producer shipped — most of the fleet
// gets a real freshness tone from day one, not a wall of "unknown".
export const repoHealthOf = (
  status: RepoHealthStatus | null | undefined,
  updatedAt: string | null | undefined,
  nowMs: number,
): RepoHealth => {
  if (status?.outcome === 'failed') {
    const d = ageDays(status.builtAt, nowMs);
    return {
      tone: 'failed',
      label:
        d === null ? 'last build failed' : `last build failed (${agoLabel(d)})`,
    };
  }
  if (
    status?.outcome === 'held-pruned' ||
    status?.outcome === 'held-zero-nodes'
  ) {
    return {
      tone: 'attention',
      label: 'publish held — still serving the previous graph',
    };
  }
  if (status?.outcome === 'empty-corpus') {
    return { tone: 'none', label: 'empty corpus — nothing to graph' };
  }
  const d = ageDays(status?.builtAt ?? updatedAt, nowMs);
  if (d === null) return { tone: 'none', label: 'no health record yet' };
  if (d > STALE_AFTER_DAYS) {
    return { tone: 'stale', label: `stale — built ${agoLabel(d)}` };
  }
  return {
    tone: 'ok',
    label: status
      ? `healthy — built ${agoLabel(d)}`
      : `published ${agoLabel(d)} (no health record yet)`,
  };
};
